import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageSquare, X, Send, Zap, RefreshCw, ChevronDown } from 'lucide-react';
import DOMPurify from 'dompurify';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const QUICK_PROMPTS = [
  { label: 'Résume ce module', icon: '📋' },
  { label: 'Génère un quiz', icon: '🎯' },
  { label: 'Explique différemment', icon: '💡' },
  { label: 'Points clés à retenir', icon: '⭐' },
];

function MarkdownText({ text }) {
  // Very simple markdown: **bold**, `code`, newlines
  const raw = text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code class="bg-white/10 px-1 py-0.5 rounded text-xs font-mono">$1</code>')
    .replace(/\n/g, '<br />');
  const html = DOMPurify.sanitize(raw, { ALLOWED_TAGS: ['strong', 'em', 'code', 'br'], ALLOWED_ATTR: ['class'] });
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

export default function ChatPanel({
  isOpen,
  onClose,
  courseId = null,
  courseTitle = null,
  moduleTitle = null,
}) {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: courseTitle
        ? `Bonjour ! Je suis Kurso IA, votre assistant pour la formation **${courseTitle}**. Posez-moi vos questions ou demandez un quiz !`
        : 'Bonjour ! Je suis Kurso IA, votre assistant pédagogique. Comment puis-je vous aider ?',
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [balance, setBalance] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      loadBalance();
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadBalance = async () => {
    try {
      const res = await axios.get(`${API}/credits/balance`, { withCredentials: true });
      setBalance(res.data.balance);
    } catch (_) {}
  };

  const sendMessage = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');
    setLoading(true);

    const userMsg = { role: 'user', content: msg };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);

    // Build history for API (exclude initial greeting)
    const apiHistory = newMessages
      .slice(1) // skip greeting
      .slice(-20)
      .map(m => ({ role: m.role, content: m.content }));

    try {
      const res = await axios.post(
        `${API}/chat/message`,
        {
          message:       msg,
          session_id:    sessionId,
          course_id:     courseId,
          course_title:  courseTitle,
          module_title:  moduleTitle,
          history:       apiHistory.slice(0, -1), // exclude current message (already in msg)
        },
        { withCredentials: true }
      );
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
      setSessionId(res.data.session_id);
      setBalance(res.data.balance_after);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erreur IA. Réessayez.';
      if (err.response?.status === 403) {
        toast.error(detail);
        setMessages(prev => [...prev, { role: 'assistant', content: `_(Crédits insuffisants — rechargez sur /credits)_` }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: `_(Erreur : ${detail})_` }]);
      }
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, sessionId, courseId, courseTitle, moduleTitle]);

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleReset = () => {
    setMessages([{
      role: 'assistant',
      content: courseTitle
        ? `Bonjour ! Je suis Kurso IA, votre assistant pour **${courseTitle}**. Nouvelle conversation démarrée.`
        : 'Nouvelle conversation démarrée. Comment puis-je vous aider ?',
    }]);
    setSessionId(null);
    setInput('');
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed right-0 top-0 h-full w-full sm:w-[380px] bg-[#16161e] border-l border-[#2a2935] z-[100] flex flex-col shadow-2xl"
      style={{ animation: 'slideInRight 0.25s ease-out' }}
      data-testid="chat-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3.5 border-b border-[#2a2935] bg-[#16161e]">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-[#20c997]/15 flex items-center justify-center">
            <MessageSquare size={16} className="text-[#20c997]" />
          </div>
          <div>
            <p className="text-sm font-bold text-white">Chat IA</p>
            <p className="text-[10px] text-[#64748b] flex items-center gap-1">
              <Zap size={9} className="text-[#ffd93d]" />
              1 crédit/message
              {balance !== null && (
                <span className="text-[#94a3b8]">· {balance} restants</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleReset}
            title="Nouvelle conversation"
            data-testid="chat-reset-btn"
            className="p-2 rounded-lg text-[#64748b] hover:text-white hover:bg-white/5 transition-colors"
          >
            <RefreshCw size={14} />
          </button>
          <button
            onClick={onClose}
            data-testid="chat-close-btn"
            className="p-2 rounded-lg text-[#64748b] hover:text-white hover:bg-white/5 transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Course context badge */}
      {courseTitle && (
        <div className="px-4 py-2 bg-[#20c997]/5 border-b border-[#2a2935]">
          <p className="text-[10px] text-[#20c997] truncate">
            Contexte : {moduleTitle ? `${moduleTitle}` : courseTitle}
          </p>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`flex gap-2.5 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
          >
            {msg.role === 'assistant' && (
              <div className="w-6 h-6 rounded-lg bg-[#20c997]/15 flex items-center justify-center shrink-0 mt-0.5">
                <MessageSquare size={11} className="text-[#20c997]" />
              </div>
            )}
            <div
              className={`rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed max-w-[85%] ${
                msg.role === 'user'
                  ? 'bg-[#ff6b6b]/15 text-white rounded-tr-sm'
                  : 'bg-[#0f0e17] border border-[#2a2935] text-[#e8e8e8] rounded-tl-sm'
              }`}
            >
              <MarkdownText text={msg.content} />
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex gap-2.5">
            <div className="w-6 h-6 rounded-lg bg-[#20c997]/15 flex items-center justify-center shrink-0">
              <MessageSquare size={11} className="text-[#20c997]" />
            </div>
            <div className="bg-[#0f0e17] border border-[#2a2935] rounded-2xl rounded-tl-sm px-4 py-3">
              <div className="flex gap-1">
                {[0,1,2].map(i => (
                  <div key={i} className="w-1.5 h-1.5 bg-[#20c997] rounded-full animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
                ))}
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick prompts */}
      {messages.length <= 1 && (
        <div className="px-4 pb-2">
          <div className="grid grid-cols-2 gap-1.5">
            {QUICK_PROMPTS.map(p => (
              <button
                key={p.label}
                onClick={() => sendMessage(p.label)}
                disabled={loading}
                className="text-xs text-left bg-[#0f0e17] border border-[#2a2935] rounded-xl px-3 py-2 text-[#94a3b8] hover:text-white hover:border-[#3a3945] transition-all disabled:opacity-50"
                data-testid={`quick-prompt-${p.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {p.icon} {p.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="px-4 pb-4 pt-2 border-t border-[#2a2935]">
        <div className="flex gap-2 items-end">
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Posez votre question..."
            rows={1}
            className="flex-1 bg-[#0f0e17] border border-[#2a2935] text-white placeholder-[#64748b] rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#20c997]/40 resize-none leading-relaxed transition-colors"
            style={{ minHeight: '42px', maxHeight: '120px' }}
            onInput={e => {
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
            data-testid="chat-input"
          />
          <button
            onClick={() => sendMessage()}
            disabled={!input.trim() || loading}
            className="w-10 h-10 bg-[#20c997] text-[#0f0e17] rounded-xl flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-opacity shrink-0"
            data-testid="chat-send-btn"
          >
            <Send size={15} />
          </button>
        </div>
        <p className="text-[10px] text-[#64748b] mt-1.5 text-center">Entrée pour envoyer · Maj+Entrée pour nouvelle ligne</p>
      </div>
    </div>
  );
}
